import { createAction } from 'redux-actions'
import { identity, get } from 'lodash/fp'
import { Action, IHash, ThunkAction } from '../types/common'
import { query } from '../common/api'
import {
  CYCLE_COUNTS_QUERY,
  CYCLE_COUNT_QUERY,
  UPDATE_CYCLE_COUNT_MUTATION,
  UPDATE_CYCLE_COUNT_ITEMS_MUTATION,
} from './cycleCountsQueries'
import { ICycleCount } from '../types/cycle-counts'

// ===== Action Types =====
export const SET_CYCLE_COUNTS = 'CYCLE_COUNTS/SET_CYCLE_COUNTS'
export type SET_CYCLE_COUNTS = Action<ICycleCount[]>
export const setCycleCounts = createAction<ICycleCount[], ICycleCount[]>(
  SET_CYCLE_COUNTS,
  identity,
)

export const SET_CURRENT_CYCLE_COUNT = 'CYCLE_COUNTS/SET_CURRENT_CYCLE_COUNT'
export type SET_CURRENT_CYCLE_COUNT = Action<ICycleCount>
export const setCurrentCycleCount = createAction<ICycleCount, ICycleCount>(
  SET_CURRENT_CYCLE_COUNT,
  identity,
)

export const UPDATE_CYCLE_COUNT = 'CYCLE_COUNTS/UPDATE_CYCLE_COUNT'
export type UPDATE_CYCLE_COUNT = Action<ICycleCount>
export const updateCycleCountInStore = createAction<ICycleCount, ICycleCount>(
  UPDATE_CYCLE_COUNT,
  identity,
)

// ===== Thunk Actions =====
export const fetchCycleCounts = (
  accountId: number,
  branchId?: number,
  locationId?: number,
): ThunkAction<Promise<ICycleCount[]>> => {
  return async (dispatch) => {
    try {
      const response = await query(CYCLE_COUNTS_QUERY, {
        accountId,
        branchId,
        locationId,
      })
      const cycleCounts = response.cycleCounts || []
      dispatch(setCycleCounts(cycleCounts))
      return cycleCounts
    } catch (e) {
      console.error('Error fetching cycle counts:', e)
      throw new Error(e.message)
    }
  }
}

export const fetchCycleCount = (
  id: number,
): ThunkAction<Promise<ICycleCount>> => {
  return async (dispatch) => {
    try {
      const response = await query(CYCLE_COUNT_QUERY, { id })
      const cycleCount = response.cycleCount
      if (cycleCount) {
        dispatch(setCurrentCycleCount(cycleCount))
      }
      return cycleCount
    } catch (e) {
      console.error('Error fetching cycle count:', e)
      throw new Error(e.message)
    }
  }
}

export const updateCycleCount = (
  id: number,
  attributes: { status?: string },
): ThunkAction<Promise<IHash<string> | null>> => {
  return async (dispatch) => {
    try {
      const response = get(
        'cycleCountUpdate',
        await query(UPDATE_CYCLE_COUNT_MUTATION, { id, attributes }),
      )

      if (!response) {
        return { base: 'Failed to update cycle count' }
      }

      if (response.errors) {
        return response.errors
      }

      if (response.result) {
        dispatch(updateCycleCountInStore(response.result))
      }

      return null
    } catch (e) {
      console.error('Error updating cycle count:', e)
      throw new Error(e.message)
    }
  }
}

export const updateCycleCountItems = (
  cycleCountId: number,
  ids: number[],
  status: 'approved' | 'rejected',
): ThunkAction<Promise<IHash<string> | null>> => {
  return async (dispatch) => {
    try {
      const response = get(
        'cycleCountItemsUpdate',
        await query(UPDATE_CYCLE_COUNT_ITEMS_MUTATION, {
          cycleCountId,
          ids,
          status,
        }),
      )

      if (!response) {
        return { base: 'Failed to update cycle count items' }
      }

      if (response.errors) {
        return response.errors
      }

      // Refresh current cycle count data after updating items
      await dispatch(fetchCycleCount(cycleCountId))

      return null
    } catch (e) {
      console.error('Error updating cycle count items:', e)
      throw new Error(e.message)
    }
  }
}
